/**
 * CUSTOM: harempants — Collection load-more enhancements
 *
 * Controller for the collection "Show more" button. The vendor LoadMoreButton
 * (assets/facets.js) delegates its click to window.CustomLoadMore.onButtonClick
 * (a minimal CUSTOM-marked hook in facets.js), so all enhanced behaviour lives
 * here in a custom- file instead of in the vendor class.
 *
 * Units (plans/plan-collection-loadmore-ux.md):
 *   U1 — "Showing X of Y" terminus, kept in sync on every load.
 *   U2 — replaceState ?page so Back returns to the page the shopper was on.
 *   U3+U4 — ONE viewport-aware "smart load" feature (flag loadmore_autoadvance):
 *        • phones (≤767px): manual "Show more" button, prefetch-warmed so the tap
 *          is instant (the old U3) — no auto-advance.
 *        • tablet/desktop: auto-advance (lazy-load from page load, no forced first
 *          click) up to a ~108-product threshold, then gate (the old U4).
 *
 * Feature flag read in priority order: the experiment assignment
 * window.Experiments.variant('loadmore_autoadvance') ('on'/'off'), assigned pre-paint
 * by snippets/custom-ab-head.liquid; else the theme setting rendered as a data-* attr on
 * #ProductGridContainer (default off). Exposure is fired (both arms) from init().
 *
 * GA4 note: assets/custom-cx.js records load_more (capture-phase click on
 * document) and re-fires view_item_list via a MutationObserver on
 * #ProductGridContainer. This controller appends into #ProductsList (a child of
 * that container), so both keep working untouched.
 */
window.CustomLoadMore = (function () {
  'use strict';

  var CARD = '.product-card';

  // U3/U4 config + state
  var config = { prefetch: false, autoAdvance: false, autoAdvanceMaxProducts: 108 };
  var prefetchStash = null;         // U3: { url, html } — at most one batch held (memory)
  var prefetchInFlight = null;      // U3: URL currently being prefetched, so repeated observer firings don't launch duplicate fetches before the stash resolves
  var prefetchIO = null;            // IntersectionObserver warming the next batch
  var autoIO = null;                // U4: IntersectionObserver triggering auto-advance
  var autoGate = 0;                 // U4: shown-count ceiling; auto-advance stops once shownCount() reaches it. A genuine user click raises it by the threshold so the next batch lazy-loads.
  var autoClicking = false;         // U4: true while we synthesize a click (don't re-raise the gate)
  var hasIO = typeof window.IntersectionObserver !== 'undefined';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* U4 runs on tablet + desktop only — never phones. Matches the vendor
     breakpoint (FoxTheme.config.mediaQueryMobile = max-width:767px). Research
     (plans/research/plp-loadmore-2024-2026-freshness-check.md): Baymard says
     don't scroll-lazy-load on mobile, and unbounded auto-append is the worst
     pattern for mobile INP/CLS — so phones keep the plain tap-to-load button. */
  function isPhone() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  }

  /* Resolve a feature flag: the experiment assignment wins, else the theme-setting
   * default. window.Experiments is built pre-paint (snippets/custom-ab-head.liquid);
   * the 'loadmore_autoadvance' registry arms are 'on' / 'off'. When the experiment is
   * disabled / the engine is absent, variant() is null and we fall back to the setting. */
  function flag(name, settingDefault) {
    try {
      if (window.Experiments && typeof window.Experiments.variant === 'function') {
        var v = window.Experiments.variant(name);
        if (v === 'on') return true;
        if (v === 'off') return false;
      }
    } catch (e) { /* fall through to setting */ }
    return settingDefault;
  }
  function readConfig() {
    var c = container();
    if (!c) return;
    // U3+U4 are merged into ONE viewport-aware feature (loadmore_autoadvance):
    //   • phones (≤767px): prefetch warms the next batch so the manual "Show more"
    //     tap is instant — no auto-advance.
    //   • tablet/desktop: auto-advance to the product threshold; no prefetch.
    // Both capabilities read the single flag; isPhone() routes them per viewport
    // (gated in prefetch()/autoAdvance()/setupObservers()).
    var on = flag('loadmore_autoadvance', c.getAttribute('data-lm-autoadvance') === 'true');
    config.autoAdvance = on; // gated to tablet/desktop downstream
    config.prefetch = on;    // gated to phones downstream
    var cap = parseInt(c.getAttribute('data-lm-autoadvance-max-products'), 10);
    config.autoAdvanceMaxProducts = isNaN(cap) || cap < 1 ? 108 : cap;
  }

  /* ---- DOM accessors (re-queried each time; the grid re-renders on filter) -- */
  function container() {
    return document.getElementById('ProductGridContainer');
  }
  function list() {
    return document.getElementById('ProductsList') || document.querySelector('.products-list');
  }
  function button() {
    return document.querySelector('button[is="load-more-button"]');
  }
  function countEl() {
    return document.querySelector('[data-loadmore-status]');
  }

  /* ---- "Showing X of Y" --------------------------------------------------- */
  function shownCount() {
    var l = list();
    return l ? l.querySelectorAll(CARD).length : 0;
  }
  function updateCount() {
    var el = countEl();
    if (!el) return;
    var tpl = el.getAttribute('data-loadmore-template');
    var total = el.getAttribute('data-loadmore-total');
    if (!tpl) return;
    // Rebuild the localized text via the @@SHOWN@@/@@TOTAL@@ tokens. textContent
    // keeps it injection-safe.
    el.textContent = tpl
      .replace('@@SHOWN@@', String(shownCount()))
      .replace('@@TOTAL@@', total != null ? total : String(shownCount()));
  }

  /* ---- Fetch URL (vendor parity: action + section_id) --------------------- */
  function sectionId(btn) {
    try {
      if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.getSectionId) {
        return FoxTheme.utils.getSectionId(btn);
      }
    } catch (e) { /* fall through */ }
    var sec = btn.closest('[data-section-id]');
    return sec ? sec.getAttribute('data-section-id') : '';
  }
  function fetchUrl(actionUrl, btn) {
    // The vendor action attr is absolute ({{ shop.url }}{{ paginate.next.url }});
    // location.href is a robust base if it's ever relative.
    var u = new URL(actionUrl, window.location.href);
    u.searchParams.set('section_id', sectionId(btn));
    return u.toString();
  }

  /* ---- U2: reflect the deepest loaded page in the URL --------------------- *
   * replaceState (not push) so the back button returns to the page the shopper
   * was on (e.g. ?page=3) instead of page 1 — no history-entry spam. Scroll +
   * (when the page is bfcache-eligible) the full loaded grid are restored by the
   * browser for free; we intentionally do NOT rebuild the grid from a stored
   * snapshot — landing on the correct page is the requirement, and a snapshot
   * rebuild is fragile across bfcache/preview and adds no value here. */
  function pageFromUrl(url) {
    var m = (url || '').match(/[?&]page=(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }
  function updateUrlPage(n) {
    if (!n) return;
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('page', n);
      u.searchParams.delete('section_id');
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) { /* no-op */ }
  }

  /* ---- Render the fetched page: append products + swap pagination --------- */
  function render(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var dstList = list();
    var srcList = doc.querySelector('.products-list');
    if (srcList && dstList) {
      var cols = srcList.querySelectorAll('.f-column');
      // appendChild moves each node; upgrading <ul is="color-swatch"> fires its
      // connectedCallback automatically so vendor swatches re-init on new cards.
      for (var i = 0; i < cols.length; i++) dstList.appendChild(cols[i]);
    }
    var c = container();
    var pag = c ? c.querySelector('.pagination') : null;
    var srcPag = doc.querySelector('.pagination');
    if (pag) {
      if (srcPag) pag.innerHTML = srcPag.innerHTML;
      else pag.remove();
    }
    updateCount();
  }

  /* ---- U3 (merged into U4): prefetch the next batch — phones only --------- *
   * On phones the loader stays a manual "Show more" button (no auto-advance), so
   * we warm the next batch as the button approaches to make the tap feel instant.
   * Tablet/desktop auto-advance instead, so they don't prefetch. */
  function prefetch() {
    if (!config.prefetch || !isPhone()) return;
    var btn = button();
    if (!btn || btn.getAttribute('data-lm-loading') === '1') return;
    var action = btn.getAttribute('action');
    if (!action) return;
    var url = fetchUrl(action, btn);
    if (prefetchStash && prefetchStash.url === url) return; // already warm
    if (prefetchInFlight === url) return;                   // a fetch for this batch is already running
    prefetchInFlight = url;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) { prefetchStash = { url: url, html: html }; })
      .catch(function () { /* leave stash null → loadNext falls back to fetch */ })
      .then(function () { if (prefetchInFlight === url) prefetchInFlight = null; });
  }

  /* ---- U4: auto-advance (lazy-load) up to a product-count threshold ------- *
   * Baymard's faithful model: load products as the shopper scrolls toward them,
   * with NO forced first click, until ~threshold products are shown, THEN
   * interrupt with the button. Tablet/desktop only (isPhone); honors
   * reduced-motion. See plans/research/plp-loadmore-2024-2026-freshness-check.md. */
  function autoAdvance() {
    if (!config.autoAdvance || reducedMotion() || isPhone()) return; // WCAG + mobile carve-out
    if (shownCount() >= autoGate) return;                // reached the threshold → require a manual click
    var btn = button();
    if (!btn || btn.getAttribute('data-lm-loading') === '1') return;
    // Synthesize a real click so the whole tracked path runs (custom-cx.js records
    // load_more + re-fires view_item_list); autoClicking stops onButtonClick from
    // re-raising the gate — only genuine user clicks do that.
    autoClicking = true;
    btn.click();
    autoClicking = false;
  }

  /* Re-target observers at the current button (button is replaced after a load).
     Also drops a stash that no longer matches the next page. */
  function setupObservers() {
    var btn = button();
    if (prefetchStash && (!btn || prefetchStash.url !== fetchUrl(btn.getAttribute('action') || '', btn))) {
      prefetchStash = null;
    }
    if (!hasIO) return;
    if (prefetchIO) prefetchIO.disconnect();
    if (autoIO) autoIO.disconnect();
    if (!btn) return;
    if (config.prefetch && isPhone()) {
      if (!prefetchIO) {
        prefetchIO = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) prefetch();
        }, { rootMargin: '800px 0px 800px 0px' });
      }
      prefetchIO.observe(btn);
    }
    if (config.autoAdvance && !reducedMotion() && !isPhone()) {
      if (!autoIO) {
        autoIO = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) autoAdvance();
        }, { rootMargin: '0px' });
      }
      autoIO.observe(btn);
    }
  }

  /* ---- Load the next batch ------------------------------------------------ */
  function loadNext(btn) {
    if (!btn || btn.getAttribute('data-lm-loading') === '1') return Promise.resolve();
    var action = btn.getAttribute('action');
    if (!action) return Promise.resolve();

    var pageNum = pageFromUrl(action);
    var url = fetchUrl(action, btn);
    btn.setAttribute('data-lm-loading', '1');
    btn.classList.add('pointer-events-none', 'btn--loading');

    // U3: serve from the warm stash for an instant reveal; else fetch.
    var source;
    if (config.prefetch && prefetchStash && prefetchStash.url === url) {
      source = Promise.resolve(prefetchStash.html);
      prefetchStash = null;
    } else {
      source = fetch(url).then(function (r) { return r.text(); });
    }

    return source
      .then(function (html) {
        render(html);
        updateUrlPage(pageNum); // U2: reflect the deepest loaded page in the URL
        setupObservers();        // re-target the warmers at the new button
      })
      .catch(function () {
        // Network/parse failure: clear loading so the user can retry. The button
        // node may have been replaced by render(); guard with a fresh lookup.
        var b = button();
        if (b) b.classList.remove('pointer-events-none', 'btn--loading');
      })
      .then(function () {
        var b = button();
        if (b) b.removeAttribute('data-lm-loading');
      });
  }

  /* ---- Public hook called by the vendor onClick delegation ---------------- */
  function onButtonClick(btn) {
    if (!container() || !list()) return false; // let the vendor handle it
    // U4: a genuine user click raises the auto-advance ceiling by the threshold
    // so lazy-loading resumes for the next batch. Synthesized auto-advance clicks
    // (autoClicking) must NOT raise it, or it would never gate. Phones never
    // auto-advance, so the gate stays put and the click just loads one batch.
    if (!autoClicking && config.autoAdvance && !isPhone()) {
      autoGate = shownCount() + config.autoAdvanceMaxProducts;
    }
    loadNext(btn);
    return true; // we've taken over
  }

  function init() {
    if (!container()) return;
    readConfig();     // U3/U4 flags (experiment assignment or theme setting)
    updateCount();    // authoritative shown count from the actual DOM
    // U4: seed the ceiling so the first ~threshold products lazy-load from page
    // load with no forced click. Phones/reduced-motion are gated downstream in
    // autoAdvance()/setupObservers(), so this is harmless there.
    autoGate = config.autoAdvance ? config.autoAdvanceMaxProducts : 0;
    setupObservers(); // U3 prefetch + U4 auto-advance observers on the button
    // A/B exposure (both arms, so on/off are comparable in GA4): the shopper is in the
    // loadmore_autoadvance experiment whenever the load-more surface is present. Buffered
    // in the head stub until custom-cx.js installs the real emitter; idempotent per page.
    if (button() || countEl()) {
      try { if (window.Experiments) window.Experiments.expose('loadmore_autoadvance'); } catch (e) { /* never break the page */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init when the section re-renders. Theme-editor / Liquid section reloads
  // fire 'shopify:section:load'. Vendor AJAX facet filtering & sorting instead
  // replace #ProductGridContainer.innerHTML and signal it with 'collection:rerendered'
  // (assets/facets.js afterRenderSection) — without this listener the observers
  // stay bound to the detached old button and auto-advance/prefetch silently die
  // after the first filter, and autoGate keeps its stale pre-filter value.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('collection:rerendered', init);

  return {
    onButtonClick: onButtonClick,
    loadNext: loadNext,
    updateCount: updateCount,
  };
})();
